'use client'

import { Analytics, type BeforeSendEvent } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'
import Script from 'next/script'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useSyncExternalStore } from 'react'
import {
  getBrowserAnalyticsConsent,
  providerConsentForSnapshot,
  subscribeToAnalyticsConsent,
  type AnalyticsConsentSnapshot,
} from '@/utils/analyticsConsent'
import { optionalAnalyticsEnabled } from '@/utils/analyticsGate'
import { capturePostHogPageview } from '@/utils/posthogClient'
import {
  GA_MEASUREMENT_ID,
  reconcileGoogleAnalyticsConsent,
  type GoogleAnalyticsTarget,
} from '@/utils/googleAnalytics'
import {
  filterSensitiveAnalyticsEvent,
  isSensitiveAnalyticsRoute,
} from '@/utils/analyticsUrl'

function keepOnlyWhileConsented(event: BeforeSendEvent): BeforeSendEvent | null {
  if (!optionalAnalyticsEnabled(getBrowserAnalyticsConsent())) return null
  return filterSensitiveAnalyticsEvent(
    event,
    typeof window === 'undefined' ? undefined : window.location.href
  )
}

interface SpeedInsightsEvent {
  type: 'vital'
  url: string
  route?: string
}

function keepSpeedInsightsOnlyWhileConsented(
  event: SpeedInsightsEvent
): SpeedInsightsEvent | null {
  if (!optionalAnalyticsEnabled(getBrowserAnalyticsConsent())) return null
  return filterSensitiveAnalyticsEvent(
    event,
    typeof window === 'undefined' ? undefined : window.location.href
  )
}

export default function OptionalAnalytics() {
  const pathname = usePathname()
  const query = useSearchParams().toString()
  const routeIsSensitive = isSensitiveAnalyticsRoute(pathname, query)
  const consent = useSyncExternalStore<AnalyticsConsentSnapshot>(
    subscribeToAnalyticsConsent,
    getBrowserAnalyticsConsent,
    () => 'loading'
  )

  // GA has a provider-level disable flag and Consent Mode state. Keep both in
  // sync so withdrawing consent stops its already-loaded runtime as well as
  // blocking all future calls through our track() helper.
  useEffect(() => {
    const providerConsent = routeIsSensitive
      ? 'denied'
      : providerConsentForSnapshot(consent)
    if (providerConsent) {
      reconcileGoogleAnalyticsConsent(
        providerConsent,
        window as unknown as GoogleAnalyticsTarget
      )
    }
  }, [consent, routeIsSensitive])

  useEffect(() => {
    if (consent === 'granted' && !routeIsSensitive) capturePostHogPageview()
  }, [consent, pathname, query, routeIsSensitive])

  if (routeIsSensitive || !optionalAnalyticsEnabled(consent)) return null

  return (
    <>
      {/* beforeSend remains installed in Vercel's runtime after unmount and
          checks the current choice, so a later withdrawal suppresses its
          automatic pageviews as well as our custom events. */}
      <Analytics beforeSend={keepOnlyWhileConsented} />
      {/* Speed Insights uses the same persisted callback behavior. Keeping its
          callback consent-aware also blocks vitals if this component unmounts
          after a withdrawal or navigation to a capability-bearing route. */}
      <SpeedInsights beforeSend={keepSpeedInsightsOnlyWhileConsented} />
      <Script
        id="ga4-library"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
    </>
  )
}
